/**
 * VAVCI · Quick-add con animación "volar a la bolsa" para las tarjetas de producto.
 *
 * Sirve a cualquier tarjeta VAVCI: rejillas del Home/relacionados (`vprod__*`) y
 * la página de colección (`vcol__*`). Reemplaza el alta nativa de Horizon por una
 * versión con feedback inmediato:
 *   1. Intercepta el submit del form `.vprod__addform` / `.vcol__addform`
 *      (previene el POST normal).
 *   2. Lanza la animación: clona la imagen de la tarjeta y la hace volar al icono
 *      del carrito → enmascara la latencia de red.
 *   3. Emite un CartLinesUpdateEvent (de @shopify/events, nativo del importmap del
 *      tema) ANTES de que vuelva la red → el badge del carrito sube al instante
 *      (cart-icon.js escucha StandardEvents.cartLinesUpdate y refresca el bubble).
 *   4. POST a /cart/add con Accept: application/javascript (+ sections= para
 *      re-render del drawer) y confirma con /cart.js, resolviendo la promesa.
 *   5. Si el servidor rechaza el alta (data.status: sin stock, regla de cantidad
 *      B2B…), lo enseña: el botón pasa al estado --error y sale un aviso
 *      role="alert" con el texto de Shopify (ya localizado) dentro de la tarjeta.
 *
 * NO abre el cart-drawer a propósito (UX: badge + vuelo). Mejora progresiva: sin
 * JS el <form> hace POST normal. Respeta prefers-reduced-motion. CSP-safe (sin
 * handlers inline). Listener delegado en document → vale para tarjetas inyectadas.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Contrato de clases: el módulo sirve a las tarjetas vprod__* (Home/relacionados),
// vcol__* (colección), vbun__* (packs & rutinas) y vcart__* (recomendaciones del carrito).
// Añadir aquí una familia nueva basta para soportarla.
const FORM_CLASSES = ['vprod__addform', 'vcol__addform', 'vbun__addform', 'vcart__addform'];
const CARD_SELECTOR = '.vprod__card, .vcol__card, .vbun__card, .vcart-card';
const BUTTON_SELECTOR = '.vprod__add, .vcol__add, .vbun__btn, .vcart__add';
const OK_CLASSES = ['vprod__add--ok', 'vcol__add--ok', 'vbun__btn--ok', 'vcart__add--ok'];
const ERROR_CLASSES = ['vprod__add--error', 'vcol__add--error', 'vbun__btn--error', 'vcart__add--error'];
// Fila precio+botón de cada tarjeta: el aviso de error se cuelga DEBAJO de ella (la
// fila es flex sin wrap; dentro quedaría apretado). Donde no hay fila, tras el form.
const ROW_SELECTOR = '.vprod__price-row, .vcol__price-row, .vcart-card__row';
const ERROR_FALLBACK = 'No se pudo añadir al carrito';

/** Icono del carrito en el header de este Horizon. */
function cartTarget() {
  return document.querySelector('cart-icon, .header-actions__cart-icon, a[href$="/cart"]');
}

function bumpCart() {
  const t = cartTarget();
  if (!t || typeof t.animate !== 'function') return;
  t.animate(
    [{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }],
    { duration: 320, easing: 'cubic-bezier(.34,1.56,.64,1)' }
  );
}

function flyToCart(form) {
  try {
    if (REDUCE) { bumpCart(); return; }
    const target = cartTarget();
    const card = form.closest(CARD_SELECTOR) || form.parentElement;
    // En tarjetas sin foto la imagen es un placeholder <span>, no <img> → sin vuelo.
    const img = card && card.querySelector('img');
    if (!target || !img || typeof img.animate !== 'function') { bumpCart(); return; }

    const src = img.getBoundingClientRect();
    const dst = target.getBoundingClientRect();
    if (!src.width || !dst.width) { bumpCart(); return; }

    const clone = img.cloneNode(true);
    clone.removeAttribute('loading');
    Object.assign(clone.style, {
      position: 'fixed', left: src.left + 'px', top: src.top + 'px',
      width: src.width + 'px', height: src.height + 'px',
      objectFit: 'cover', borderRadius: '9999px', margin: '0',
      zIndex: '99999', pointerEvents: 'none',
      boxShadow: '0 12px 30px rgba(0,0,0,.28)', willChange: 'transform, opacity',
    });
    document.body.appendChild(clone);

    const dx = dst.left + dst.width / 2 - (src.left + src.width / 2);
    const dy = dst.top + dst.height / 2 - (src.top + src.height / 2);

    const anim = clone.animate(
      [
        { transform: 'translate(0,0) scale(1)', opacity: 1, offset: 0 },
        { transform: 'translate(' + dx * 0.5 + 'px,' + (dy * 0.5 - 48) + 'px) scale(.55)', opacity: 1, offset: 0.55 },
        { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.1)', opacity: 0.35, offset: 1 },
      ],
      { duration: 680, easing: 'cubic-bezier(.5,.05,.55,.95)' }
    );
    anim.onfinish = function () { clone.remove(); bumpCart(); };
    anim.oncancel = function () { clone.remove(); };
  } catch (e) { bumpCart(); }
}

/**
 * Feedback visible cuando /cart/add rechaza el alta. Estilos por Object.assign
 * (CSP-safe, como el clon del vuelo). Un solo aviso por tarjeta: si ya existe
 * ([data-vc-qa-error]) se reutiliza y se reinicia su temporizador.
 */
function showError(form, button, message) {
  if (button) {
    ERROR_CLASSES.forEach((c) => button.classList.add(c));
    setTimeout(() => ERROR_CLASSES.forEach((c) => button.classList.remove(c)), 2400);
  }
  const card = form.closest(CARD_SELECTOR) || form.parentElement;
  if (!card) return;
  let note = card.querySelector('[data-vc-qa-error]');
  if (!note) {
    note = document.createElement('p');
    note.setAttribute('role', 'alert');
    note.setAttribute('data-vc-qa-error', '');
    Object.assign(note.style, {
      margin: '6px 0 0', fontSize: '12px', lineHeight: '1.35', fontWeight: '500',
      color: '#b3261e', fontFamily: 'var(--vc-sans, inherit)',
    });
    const anchor = form.closest(ROW_SELECTOR) || form;
    anchor.insertAdjacentElement('afterend', note);
  }
  note.textContent = message;
  clearTimeout(note.vcTimer);
  note.vcTimer = setTimeout(() => note.remove(), 4000);
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !FORM_CLASSES.some((c) => form.classList.contains(c))) return;
  event.preventDefault();
  // Dentro del carrito (drawer modal o /cart) el icono del header queda detrás
  // o fuera de vista: sin vuelo, solo el "bump" del badge y el re-render normal.
  if (form.closest('[data-vc-cart-recs]')) bumpCart();
  else flyToCart(form);
  addToCart(form);
});

async function addToCart(form) {
  const button = form.querySelector(BUTTON_SELECTOR);
  if (button && button.getAttribute('aria-busy') === 'true') return;
  if (button) button.setAttribute('aria-busy', 'true');

  const formData = new FormData(form);

  // Pide el render de las secciones del carrito para refrescarlas sin recargar.
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  });
  if (sectionIds.size) formData.set('sections', [...sectionIds].join(','));

  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  // Evento optimista: cart-icon.js actualiza el badge al instante.
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(form.action, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await response.json();

    if (data.status) { // rechazo del servidor (sin stock, regla de cantidad B2B…)
      deferred.resolve({ detail: { didError: true, source: 'vavci-quick-add' } });
      showError(form, button, data.description || data.message || ERROR_FALLBACK);
      return;
    }

    const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'vavci-quick-add', itemCount: quantity, sections: data.sections, didError: false },
    });

    if (button) {
      OK_CLASSES.forEach((c) => button.classList.add(c));
      setTimeout(() => OK_CLASSES.forEach((c) => button.classList.remove(c)), 1600);
    }
  } catch (error) {
    if (typeof deferred.reject === 'function') deferred.reject(error);
    console.warn('[vavci-quick-add] add to cart failed:', error);
  } finally {
    if (button) button.removeAttribute('aria-busy');
  }
}
